/**
 * FleetContractsOverview — Vue consolidée des contrats du parc auto.
 * Parité BE-TAYE `ContractsOverview.jsx` fleet.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { apiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FileSignature, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABEL = { leasing: "Leasing", lld: "LLD", loa: "LOA",
                       insurance: "Assurance", maintenance: "Entretien", inspection_ct: "CT" };
const TYPE_COLOR = { leasing: "bg-blue-50 text-blue-700 border-blue-200",
                       lld: "bg-indigo-50 text-indigo-700 border-indigo-200",
                       loa: "bg-violet-50 text-violet-700 border-violet-200",
                       insurance: "bg-emerald-50 text-emerald-700 border-emerald-200",
                       maintenance: "bg-amber-50 text-amber-700 border-amber-200",
                       inspection_ct: "bg-slate-100 text-slate-700 border-slate-200" };

export default function FleetContractsOverview() {
  const navigate = useNavigate();
  const [data, setData] = useState({ contracts: [], total_monthly: 0, alerts_60d: 0 });
  const [type, setType] = useState("all");
  const [q, setQ] = useState("");

  const load = async () => {
    try {
      const params = type !== "all" ? { contract_type: type } : {};
      const r = await api.get("/fleet/contracts", { params });
      setData(r.data);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [type]);

  const filtered = data.contracts.filter(c => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (c.plate || "").toLowerCase().includes(s)
      || (c.provider || "").toLowerCase().includes(s)
      || (c.brand || "").toLowerCase().includes(s)
      || (c.model || "").toLowerCase().includes(s);
  });

  return (
    <div className="space-y-6" data-testid="fleet-contracts-overview">
      <div>
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
          Contrats parc auto
        </h1>
        <p className="text-sm text-slate-500">
          {data.count || filtered.length} contrat{filtered.length > 1 ? "s" : ""} · coût mensuel actif{" "}
          <span className="font-mono font-semibold text-slate-700">
            {(data.total_monthly || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €
          </span>
          {data.alerts_60d > 0 && (
            <> · <span className="text-red-600 font-medium">{data.alerts_60d} échéance{data.alerts_60d > 1 ? "s" : ""} &lt; 60 j</span></>
          )}
        </p>
      </div>

      <Card><CardContent className="p-4 flex flex-col md:flex-row gap-3">
        <Input value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Rechercher immat, fournisseur, marque..." className="max-w-md"
          data-testid="fleet-ctr-search" />
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="max-w-xs" data-testid="fleet-ctr-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous types</SelectItem>
            {Object.entries(TYPE_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
      </CardContent></Card>

      <Card><CardContent className="p-0">
        {filtered.length === 0 ? (
          <div className="p-12 text-center">
            <FileSignature className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">Aucun contrat.</p>
          </div>
        ) : (
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead className="text-xs uppercase">Type</TableHead>
              <TableHead className="text-xs uppercase">Immat</TableHead>
              <TableHead className="text-xs uppercase">Véhicule</TableHead>
              <TableHead className="text-xs uppercase">Fournisseur</TableHead>
              <TableHead className="text-xs uppercase">Début</TableHead>
              <TableHead className="text-xs uppercase">Fin</TableHead>
              <TableHead className="text-xs uppercase text-right">Coût / mois</TableHead>
              <TableHead className="text-xs uppercase">Alerte</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filtered.map((c, i) => (
                <motion.tr key={c.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  transition={{ delay: Math.min(i, 15) * 0.015 }}
                  className="border-b hover:bg-slate-50 cursor-pointer"
                  onClick={() => navigate(`/app/fleet/vehicles/${c.vehicle_id}`)}
                  data-testid={`fleet-ctr-row-${i}`}>
                  <TableCell><Badge variant="outline" className={TYPE_COLOR[c.contract_type] || ""}>{TYPE_LABEL[c.contract_type] || c.contract_type}</Badge></TableCell>
                  <TableCell className="font-mono font-semibold">{c.plate}</TableCell>
                  <TableCell className="text-sm">{c.brand} {c.model}</TableCell>
                  <TableCell className="text-sm">{c.provider || "—"}</TableCell>
                  <TableCell className="font-mono text-sm">{c.start_date ? new Date(c.start_date).toLocaleDateString("fr-FR") : "—"}</TableCell>
                  <TableCell className="font-mono text-sm">{c.end_date ? new Date(c.end_date).toLocaleDateString("fr-FR") : "—"}</TableCell>
                  <TableCell className="text-right font-mono">{c.monthly_cost ? `${c.monthly_cost.toFixed(2)} €` : "—"}</TableCell>
                  <TableCell>
                    {c.severity === "critical" ? (
                      <Badge variant="outline" className="bg-red-100 text-red-700 border-red-200">
                        <AlertTriangle className="w-3 h-3 mr-1" />J-{c.days_left}
                      </Badge>
                    ) : c.severity === "warning" ? (
                      <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-200">J-{c.days_left}</Badge>
                    ) : (
                      <span className="text-xs text-slate-400">—</span>
                    )}
                  </TableCell>
                </motion.tr>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent></Card>
    </div>
  );
}
